import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import api from '../api';
import Modal from '../Modal';
import ConfirmationModal from '../ConfirmationModal';

// Import des sous-composants
import RoleBulkActions from './role/RoleBulkActions';
import RoleFilters from './role/RoleFilters';
import RolesList from './role/RolesList';
import RoleForm from './role/RoleForm';

const RoleTab = () => {
  // États pour les données
  const [roles, setRoles] = useState([]);
  
  // États pour le chargement
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  
  // États pour la recherche
  const [searchTerm, setSearchTerm] = useState('');
  
  // États pour les modals
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [roleToDelete, setRoleToDelete] = useState(null);
  
  // États pour le formulaire
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });
  const [formErrors, setFormErrors] = useState({});
  
  // États pour la pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize] = useState(12);
  
  // États pour la sélection multiple
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);

  // Debounce pour la recherche
  const [searchDebounce, setSearchDebounce] = useState('');

  // Effect pour le debounce de la recherche
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchDebounce(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Effect pour charger les données
  useEffect(() => {
    fetchRoles();
  }, [currentPage, searchDebounce]);

  const fetchRoles = async (page = currentPage) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: pageSize.toString()
      });

      if (searchDebounce.trim()) params.append('search', searchDebounce.trim());

      const response = await api.get(`/utilisateurs/roles/?${params}`);
      setRoles(response.data.results || []);
      setTotalPages(Math.ceil((response.data.count || 0) / pageSize));
      setTotalCount(response.data.count || 0);
    } catch (error) {
      console.error('Erreur lors du chargement des rôles:', error);
      toast.error('Erreur lors du chargement des rôles');
    } finally {
      setLoading(false);
    }
  };

  // Gestion du formulaire
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear l'erreur si l'utilisateur corrige le champ
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Ouvrir le modal
  const handleOpenModal = (role = null) => {
    if (role) {
      setSelectedRole(role);
      setFormData({
        name: role.name || '',
        description: role.description || ''
      });
    } else {
      setSelectedRole(null);
      setFormData({
        name: '',
        description: ''
      });
    }
    setFormErrors({});
    setIsModalOpen(true);
  };

  // Validation du formulaire
  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = "Le nom du rôle est requis";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Soumettre le formulaire
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSubmitting(true);
    try {
      const data = {
        name: formData.name.trim(),
        description: formData.description.trim()
      };

      if (selectedRole) {
        await api.patch(`/utilisateurs/roles/${selectedRole.id}/`, data);
        toast.success("Rôle modifié avec succès");
      } else {
        await api.post('/utilisateurs/roles/', data);
        toast.success("Rôle créé avec succès");
      }
      
      fetchRoles();
      setIsModalOpen(false);
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      if (error.response?.data?.name) {
        toast.error("Ce nom de rôle existe déjà");
      } else {
        toast.error(error.response?.data?.error || "Une erreur est survenue");
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Gestion de la suppression
  const handleOpenDeleteModal = (role) => {
    setRoleToDelete(role);
    setIsConfirmModalOpen(true);
  };

  const handleDelete = async () => {
    if (!roleToDelete) return;
    
    setDeleteLoading(true);
    try {
      await api.delete(`/utilisateurs/roles/${roleToDelete.id}/`);
      toast.success("Rôle supprimé avec succès");
      fetchRoles();
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      if (error.response?.status === 400) {
        toast.error("Impossible de supprimer ce rôle car il est assigné à des utilisateurs");
      } else {
        toast.error("Impossible de supprimer ce rôle. Veuillez réessayer.");
      }
    } finally {
      setDeleteLoading(false);
      setIsConfirmModalOpen(false);
    }
  };

  // Fonctions pour les actions en lot
  const handleBulkDelete = async () => {
    if (selectedRoles.length === 0) {
      toast.error('Veuillez sélectionner au moins un rôle');
      return;
    }

    setBulkActionLoading(true);
    try {
      const promises = selectedRoles.map(roleId => 
        api.delete(`/utilisateurs/roles/${roleId}/`)
      );
      
      await Promise.all(promises);
      toast.success(`${selectedRoles.length} rôle(s) supprimé(s) avec succès`);
      setSelectedRoles([]);
      fetchRoles();
    } catch (error) {
      console.error('Erreur lors de la suppression en lot:', error);
      toast.error('Erreur lors de la suppression des rôles');
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Fonction pour sélectionner/désélectionner un rôle
  const handleSelectRole = (roleId) => {
    setSelectedRoles(prev => 
      prev.includes(roleId) 
        ? prev.filter(id => id !== roleId)
        : [...prev, roleId]
    );
  };

  // Fonction pour sélectionner/désélectionner tous les rôles
  const handleSelectAll = () => {
    if (selectedRoles.length === roles.length) {
      setSelectedRoles([]);
    } else {
      setSelectedRoles(roles.map(r => r.id));
    }
  };

  return (
    <div className="space-y-6">
      {/* Actions en lot */}
      <RoleBulkActions
        selectedRoles={selectedRoles}
        bulkActionLoading={bulkActionLoading}
        onBulkDelete={handleBulkDelete}
        onOpenModal={handleOpenModal}
      />

      {/* Filtres */}
      <RoleFilters
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        totalRoles={totalCount}
      />

      {/* Liste des rôles */}
      <RolesList
        roles={roles}
        loading={loading}
        selectedRoles={selectedRoles}
        onSelectRole={handleSelectRole}
        onSelectAll={handleSelectAll}
        onOpenModal={handleOpenModal}
        onOpenDeleteModal={handleOpenDeleteModal}
        currentPage={currentPage}
        totalPages={totalPages}
        setCurrentPage={setCurrentPage}
      />

      {/* Modal de création/modification */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={selectedRole ? 'Modifier le rôle' : 'Nouveau rôle'}
        maxWidth="max-w-2xl"
      >
        <RoleForm
          formData={formData}
          formErrors={formErrors}
          onInputChange={handleInputChange}
          onSubmit={handleSubmit}
          submitting={submitting}
          selectedRole={selectedRole}
          onCancel={() => setIsModalOpen(false)}
        />
      </Modal>

      {/* Modal de confirmation de suppression */}
      <ConfirmationModal 
        isOpen={isConfirmModalOpen}
        onClose={() => setIsConfirmModalOpen(false)}
        onConfirm={handleDelete}
        title="Supprimer le rôle"
        message={`Êtes-vous sûr de vouloir supprimer le rôle "${roleToDelete?.name}" ?`}
        confirmText="Supprimer"
        cancelText="Annuler"
        type="danger"
        isLoading={deleteLoading}
      />
    </div>
  );
};

export default RoleTab;
